import React from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "../i18n/I18nProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";

interface QuoteSuccessStepProps {
  policyId?: string | null;
  planName?: string | null;
  planCarrier?: string | null;
  planPremium?: number | null;
  selectedHouseholdTypeIcon?: string | null;
  onFinish?: () => void;
}

const formatCurrency = (value: number | null | undefined, locale: string) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
};

export const QuoteSuccessStep: React.FC<QuoteSuccessStepProps> = ({
  policyId,
  planName,
  planCarrier,
  planPremium,
  selectedHouseholdTypeIcon,
  onFinish,
}) => {
  const { t, language } = useTranslation();
  const hasPlanDetails = Boolean(
    planName || planCarrier || planPremium !== null
  );
  const formattedPremium = formatCurrency(
    typeof planPremium === "number" ? planPremium : null,
    language
  );

  return (
    <div className="card">
      <div className="card-body">
        <div className="quote-success">
          <h3 className="card-title">{t("quote.success.title")}</h3>

          <div className="quote-success-message">
            <div
              className={
                selectedHouseholdTypeIcon
                  ? "household-type-selection-header"
                  : ""
              }
            >
              <div>
                <p className="quote-success-highlight">
                  <strong>{t("quote.success.messageTitle")}</strong>
                </p>
                <p>{t("quote.success.messageBody")}</p>
              </div>
              {selectedHouseholdTypeIcon && (
                <div className="household-type-selected">
                  <span aria-hidden="true" className="household-type-icon">
                    {selectedHouseholdTypeIcon}
                    <FontAwesomeIcon
                      className="fa-check step-success"
                      icon={faCircleCheck}
                    />
                  </span>
                </div>
              )}
            </div>
            <p>
              <div
                dangerouslySetInnerHTML={{
                  __html: t("quote.success.messageFollowUp"),
                }}
              ></div>
              <em>{}</em>
            </p>
          </div>

          {hasPlanDetails && (
            <div className="quote-success-plan">
              <h4>{t("quote.success.planSummaryTitle")}</h4>
              <ul>
                {planName && (
                  <li>
                    <span>{t("quote.success.planName")}:</span>
                    <strong>{planName}</strong>
                  </li>
                )}
                {planCarrier && (
                  <li>
                    <span>{t("quote.success.planCarrier")}:</span>
                    <strong>{planCarrier}</strong>
                  </li>
                )}
                {planPremium !== null && !Number.isNaN(planPremium) && (
                  <li>
                    <span>{t("quote.success.planPremium")}:</span>
                    <strong>{formattedPremium}</strong>
                  </li>
                )}
              </ul>
            </div>
          )}

          {policyId && (
            <p className="quote-success-id">
              {t("quote.success.policyId", { policyId })}
            </p>
          )}

          <Button
            className="button button-primary quote-success-finish"
            onClick={() => onFinish?.()}
          >
            {t("quote.success.finish")}
          </Button>
        </div>
      </div>
    </div>
  );
};
